import {requirePermission} from '@/modules/auth/permissions'
import {useGetInfiniteHl7Events} from '@/modules/hl7-events/api'
import {openHl7EventDrawer} from '@/modules/hl7-events/components/hl7-event-drawer/util'
import {Hl7EventsHeader} from '@/modules/hl7-events/components/hl7-events-header'
import {Hl7Table} from '@/modules/hl7-events/components/hl7-table'
import {Hl7SearchSchema} from './schemas'
import {useAppForm} from '@cypro/ui/integrations/form'
import {toaster} from '@cypro/ui/toaster'
import {Box, HStack, Stack, Tabs, Text} from '@chakra-ui/react'
import {createFileRoute} from '@tanstack/react-router'
import {useTranslate} from '@tolgee/react'
import {FormEvent, useState} from 'react'
import * as sdk from '@cypro/server/sdk'
import {HL7Direction} from '@cypro/engine/database/schema'
import {Hl7EventLog} from '@cypro/engine/schema'

export const Route = createFileRoute('/configuration/hl7/')({
  beforeLoad: requirePermission('interfaces.manage'),
  component: RouteComponent,
  validateSearch: Hl7SearchSchema,
})

const LIMIT = 50

function RouteComponent() {
  const {t} = useTranslate('common')

  const form = useAppForm({
    defaultValues: {message: ''},
    async onSubmit(props) {
      if (props.value.message.length > 0) {
        try {
          await sdk.postHl7({body: {message: props.value.message}})
          form.reset()
          toaster.success({title: 'Message Sent.'})
        } catch (e) {
          console.error(e)
          toaster.error({title: 'An error has occurred'})
        }
      }
    },
  })

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    form.handleSubmit()
  }

  const [direction, setDirection] = useState<HL7Direction>('INBOUND')

  return (
    <>
      <Box borderWidth="1px" borderRadius="md" p="4" mt="6" bg="bg.panel">
        <form onSubmit={onSubmit}>
          <form.AppForm>
            <Stack gap="3">
              <Text textStyle="sm" fontWeight="medium">
                {t('send_hl7_message')}
              </Text>
              <form.AppField name="message">
                {field => <field.Textarea label="HL7 Message" hideLabel placeholder="Message" rows={6} />}
              </form.AppField>
              <HStack justifyContent="flex-end">
                <form.SubmitButton>{t('send')}</form.SubmitButton>
              </HStack>
            </Stack>
          </form.AppForm>
        </form>
      </Box>

      <Box flexShrink={0} marginTop="4">
        <Hl7EventsHeader />
      </Box>

      <Tabs.Root
        value={direction === 'INBOUND' ? 'inbound' : 'outbound'}
        onValueChange={details => setDirection(details.value === 'inbound' ? 'INBOUND' : 'OUTBOUND')}
      >
        <Tabs.List>
          <Tabs.Trigger value="inbound" data-e2e="inbound">
            {t('inbound')}
          </Tabs.Trigger>
          <Tabs.Trigger value="outbound" data-e2e="outbound">
            {t('outbound')}
          </Tabs.Trigger>
        </Tabs.List>
      </Tabs.Root>
      <Hl7EventsTable direction={direction} />
    </>
  )
}

function Hl7EventsTable({direction}: {direction: HL7Direction}) {
  const search = Route.useSearch()

  const {getPageQuery, getSingleQuery, toStartRow, listContextParams} = useGetInfiniteHl7Events({
    limit: LIMIT,
    direction,
    filters: {
      status: search.filters?.status,
      createdAtFrom: search.filters?.createdAtFrom,
      createdAtTo: search.filters?.createdAtTo,
    },
  })

  return (
    <Hl7Table
      virtualizer={{getPageQuery, getSingleQuery, toStartRow, listContextParams}}
      table={{}}
      onRowClick={(event: Hl7EventLog) => {
        openHl7EventDrawer(event)
      }}
    />
  )
}
